import React from 'react';

import {SeasonalMetricSet} from '../../lib/api/eval';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const BAR_COLOR = '#3b82f6'; // blue-500
const BAR_COLOR_HIGHLIGHT = '#f59e0b'; // amber-500 for spring melt months

const HEIGHT = 240;
const MARGIN = {top: 24, right: 16, bottom: 32, left: 48};

function metricColor(metric: string, value: number): string {
  if (metric === 'kge') {
    return value > 0.7 ? '#16a34a' : value > 0.4 ? '#ca8a04' : '#dc2626';
  }
  if (metric === 'nrmse') {
    return value < 0.3 ? '#16a34a' : value < 0.6 ? '#ca8a04' : '#dc2626';
  }
  return BAR_COLOR;
}

type MetricKey = 'kge' | 'nrmse' | 'mae' | 'percentBias';

export default function CalendarMonthChart({
  data,
  metric = 'kge',
  highlightMonths,
}: {
  data?: Record<string, SeasonalMetricSet>;
  metric?: MetricKey;
  highlightMonths?: Set<number>;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(600);
  const [hoveredMonth, setHoveredMonth] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(Math.round(entry.contentRect.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!data) {
    return <div className="text-sm text-gray-400 text-center py-8">No monthly data available</div>;
  }

  const values: Array<{month: number; label: string; value: number | null; count: number}> = [];
  for (let m = 1; m <= 12; m++) {
    const entry = data[String(m)];
    const raw = entry?.[metric];
    values.push({
      month: m,
      label: MONTH_LABELS[m - 1],
      value: raw != null && Number.isFinite(raw) ? raw : null,
      count: entry?.forecastCount ?? 0,
    });
  }

  const validValues = values.filter((v) => v.value !== null).map((v) => v.value!);
  if (validValues.length === 0) {
    return <div className="text-sm text-gray-400 text-center py-8">No monthly data available</div>;
  }

  const usesZeroBaseline = metric === 'kge' || metric === 'percentBias';
  const minVal = Math.min(...validValues, ...(usesZeroBaseline ? [0] : []));
  const maxVal = Math.max(...validValues, ...(usesZeroBaseline ? [0] : []));
  const range = maxVal - minVal || 1;
  const yMin = minVal - range * 0.1;
  const yMax = maxVal + range * 0.1;

  const graphW = width - MARGIN.left - MARGIN.right;
  const graphH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const barW = graphW / 12 - 4;

  const yScale = (v: number) => MARGIN.top + graphH - ((v - yMin) / (yMax - yMin)) * graphH;
  const xScale = (m: number) => MARGIN.left + ((m - 1) / 12) * graphW + graphW / 24;

  // Reference line at 0 if metric includes negative values (KGE, percentBias)
  const showZeroLine = (metric === 'kge' || metric === 'percentBias') && yMin < 0 && yMax > 0;

  return (
    <div ref={containerRef}>
      <svg viewBox={`0 0 ${width} ${HEIGHT}`} width="100%" height={HEIGHT}>
        {/* Y axis */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const val = yMin + frac * (yMax - yMin);
          const y = yScale(val);
          return (
            <g key={frac}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth={0.5}
              />
              <text x={MARGIN.left - 4} y={y} textAnchor="end" fontSize={10} fill="#6b7280" dy={3}>
                {val.toFixed(2)}
              </text>
            </g>
          );
        })}

        {showZeroLine && (
          <line
            x1={MARGIN.left}
            x2={width - MARGIN.right}
            y1={yScale(0)}
            y2={yScale(0)}
            stroke="#9ca3af"
            strokeWidth={1}
            strokeDasharray="4 2"
          />
        )}

        {/* Bars */}
        {values.map((v) => {
          const x = xScale(v.month);
          const isHighlight = highlightMonths?.has(v.month);
          const isHovered = hoveredMonth === v.month;

          if (v.value === null) {
            return (
              <g key={v.month}>
                <text
                  x={x}
                  y={HEIGHT - MARGIN.bottom + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fill="#9ca3af"
                >
                  {v.label}
                </text>
              </g>
            );
          }

          const barTop = yScale(v.value);
          const baseline = yScale(metric === 'kge' || metric === 'percentBias' ? 0 : yMin);
          const barHeight = Math.abs(baseline - barTop);
          const barY = Math.min(barTop, baseline);
          const color = isHighlight ? BAR_COLOR_HIGHLIGHT : metricColor(metric, v.value);

          return (
            <g
              key={v.month}
              onMouseEnter={() => setHoveredMonth(v.month)}
              onMouseLeave={() => setHoveredMonth(null)}
              style={{cursor: 'default'}}
            >
              <rect
                x={x - barW / 2}
                y={barY}
                width={barW}
                height={Math.max(barHeight, 1)}
                fill={color}
                fillOpacity={isHovered ? 0.9 : 0.7}
                stroke={color}
                strokeWidth={isHovered ? 1.5 : 0.5}
                rx={1}
              />
              {isHovered && (
                <text
                  x={x}
                  y={barY - 4}
                  textAnchor="middle"
                  fontSize={9}
                  fill="#374151"
                  fontWeight="bold"
                >
                  {v.value.toFixed(2)} (n={v.count})
                </text>
              )}
              <text
                x={x}
                y={HEIGHT - MARGIN.bottom + 16}
                textAnchor="middle"
                fontSize={10}
                fill={isHighlight ? '#92400e' : '#374151'}
                fontWeight={isHighlight ? 'bold' : 'normal'}
              >
                {v.label}
              </text>
            </g>
          );
        })}

        {/* Metric label */}
        <text
          x={MARGIN.left / 2}
          y={MARGIN.top + graphH / 2}
          textAnchor="middle"
          fontSize={11}
          fill="#6b7280"
          transform={`rotate(-90, ${MARGIN.left / 2}, ${MARGIN.top + graphH / 2})`}
        >
          {metric === 'kge'
            ? 'KGE'
            : metric === 'nrmse'
              ? 'nRMSE'
              : metric === 'mae'
                ? 'MAE'
                : '%Bias'}
        </text>
      </svg>
    </div>
  );
}
